// Qualtrics to LimeSurvey question type mapping
export const QUALTRICS_TO_LIMESURVEY_TYPE_MAPPING = {
  // Multiple Choice questions
  'MC': (qualtricsQuestion) => {
    const selector = qualtricsQuestion.Selector;
    switch (selector) {
      case 'MAVR': // Multiple Answer Vertical Radio (checkboxes)
        return 'M'; // Multiple choice with checkboxes
      case 'SAVR': // Single Answer Vertical Radio 
        return 'L'; // List (radio)
      case 'DL': // Drop List
        return 'L'; // List (dropdown)
      default:
        return 'L'; // Default to List
    }
  },
  
  // Text Entry questions
  'TE': (qualtricsQuestion) => {
    const selector = qualtricsQuestion.Selector;
    switch (selector) {
      case 'SL': // Single Line
        // Check validation for number input
        if (qualtricsQuestion.Validation?.Settings?.ContentType === 'ValidNumber') {
          return 'N'; // Numerical input
        }
        return 'S'; // Short free text
      case 'FORM': // Form (multiple text boxes)
        return 'T'; // Long free text
      case 'ML': // Multi Line
        return 'T'; // Long free text
      default:
        return 'T'; // Default to Long free text
    }
  },
  
  // Matrix questions
  'Matrix': (qualtricsQuestion) => {
    const selector = qualtricsQuestion.Selector;
    const subSelector = qualtricsQuestion.SubSelector;
    
    switch (selector) {
      case 'Likert':
        if (subSelector === 'SingleAnswer') {
          return 'F'; // Array (radio buttons)
        } else if (subSelector === 'MultipleAnswer') {
          return 'A'; // Array (checkboxes)
        }
        return 'F'; // Default Array
      case 'TE': // Text Entry Matrix
        return 'F'; // Array with text inputs
      case 'MaxDiff':
        return 'F'; // Array
      default:
        return 'F'; // Default Array
    }
  },
  
  // Ranking questions
  'RO': 'R', // Ranking
  
  // Slider questions
  'Slider': 'K', // Multiple numerical input
  
  // HeatMap questions
  'HeatMap': 'I',
  
  // Constant Sum questions
  'CS': 'K', // Multiple numerical input
  
  // Display/Boilerplate text
  'DB': 'X', // Display/Boilerplate text
  
  // Side by Side
  'SBS': 'F', // Array
};

// Supported Qualtrics question types list
export const SUPPORTED_QUALTRICS_QUESTION_TYPES = [
  'MC',      // Multiple Choice
  'TE',      // Text Entry
  'Matrix',  // Matrix/Likert
  'RO',      // Ranking
  'Slider',  // Slider
  'HeatMap', // HeatMap
  'CS',      // Constant Sum
  'DB',      // Display/Boilerplate
  'SBS'      // Side by Side
];

// Helper function to check if a Qualtrics question type is supported
export const isQualtricsQuestionTypeSupported = (qualtricsQuestion) => {
  if (!qualtricsQuestion || !qualtricsQuestion.QuestionType) {
    return false;
  }
  
  return SUPPORTED_QUALTRICS_QUESTION_TYPES.includes(qualtricsQuestion.QuestionType);
};

// Helper function to get LimeSurvey type from Qualtrics question
export const getQualtricsToLimeSurveyType = (qualtricsQuestion) => {
  const questionType = qualtricsQuestion.QuestionType;
  const mapping = QUALTRICS_TO_LIMESURVEY_TYPE_MAPPING[questionType];
  
  if (typeof mapping === 'function') {
    return mapping(qualtricsQuestion);
  }
  
  return mapping || 'L'; // Default to List if not found
};

// Helper function to filter out unsupported Qualtrics questions
export const filterSupportedQualtricsQuestions = (qualtricsQuestions) => {
  if (!Array.isArray(qualtricsQuestions)) {
    return [];
  }
  
  return qualtricsQuestions.filter((question) => {
    const isSupported = isQualtricsQuestionTypeSupported(question);
    
    // Log unsupported question types for debugging
    if (!isSupported && question.QuestionType) {
      console.warn(`Unsupported Qualtrics question type: ${question.QuestionType} (Question ID: ${question.QuestionID || 'unknown'})`);
    }
    
    return isSupported;
  });
};

// Helper function to determine if question allows multiple answers
export const isQualtricsMultipleAnswer = (qualtricsQuestion) => {
  const questionType = qualtricsQuestion.QuestionType;
  const selector = qualtricsQuestion.Selector;
  const subSelector = qualtricsQuestion.SubSelector;
  
  if (questionType === 'MC' && selector === 'MAVR') {
    return true; // Multiple choice with checkboxes
  }
  
  if (questionType === 'Matrix' && selector === 'Likert' && subSelector === 'MultipleAnswer') {
    return true; // Matrix with multiple answers
  }
  
  return false;
};

// Helper function to check if question has "other" option
export const qualtricsHasOtherOption = (qualtricsQuestion) => {
  if (!qualtricsQuestion.Choices) return false;
  
  return Object.values(qualtricsQuestion.Choices).some(choice => 
    choice.TextEntry === 'true' || choice.TextEntry === true
  );
}; 